import { useState } from "react";
import { motion } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];

const BELIEFS = [
  {
    n: "01",
    text: "Being tired all the time is not normal.",
    tag: null,
    sub: "When did being exhausted become something we’re just supposed to live with?",
    bg: "#D9C6F2",
    deep: "#A87FD4",
    orb: "#CBB5E8",
    icon: "moon",
  },
  {
    n: "02",
    text: "You shouldn’t have to earn the right to rest.",
    tag: null,
    sub: "Why do we feel guilty for doing the one thing our bodies actually need?",
    bg: "#F6C6D8",
    deep: "#E58CAB",
    orb: "#F3B9CD",
    icon: "spark",
  },
  {
    n: "03",
    text: "We should listen to our bodies before they have to scream for our attention.",
    tag: null,
    sub: "Why do we only take our bodies seriously when they stop cooperating?",
    bg: "#C6CDEE",
    deep: "#8E9BD4",
    orb: "#C3CBEF",
    icon: "heart",
  },
  {
    n: "04",
    text: "An afternoon nap hits 10x harder than a full night’s sleep.",
    tag: "[Completely unrelated]",
    sub: "There is absolutely no scientific explanation for why this is true. but it just is",
    bg: "#BAD7F2",
    deep: "#7FAFDC",
    orb: "#9CC0EA",
    icon: "sun",
  },
];

const GRAPHICS = {
  moon: (c) => (
    <svg viewBox="0 0 100 100" className="h-full w-full" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M50 8a42 42 0 1 0 0 84 42 42 0 0 0 0-84Zm12 8a34 34 0 1 1 0 68 34 34 0 0 1 0-68Z"
        fill={c}
      />
    </svg>
  ),
  spark: (c) => (
    <svg viewBox="0 0 100 100" className="h-full w-full" aria-hidden="true">
      <path d="M50 2 59 41 98 50 59 59 50 98 41 59 2 50 41 41Z" fill={c} />
    </svg>
  ),
  heart: (c) => (
    <svg viewBox="0 0 24 24" className="h-full w-full" aria-hidden="true">
      <path
        d="M12 20.5s-6.6-4.1-9-7.9C1.2 9.4 2.9 5.6 6.5 5.6c2.1 0 3.8 1.2 5.5 3.3 1.7-2.1 3.4-3.3 5.5-3.3 3.6 0 5.3 3.8 3.5 7-2.4 3.8-9 7.9-9 7.9Z"
        fill={c}
      />
    </svg>
  ),
  sun: (c) => (
    <svg viewBox="0 0 100 100" className="h-full w-full" aria-hidden="true">
      <circle cx="50" cy="50" r="22" fill={c} />
      {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => (
        <rect
          key={a}
          x="46.5"
          y="6"
          width="7"
          height="17"
          rx="3.5"
          fill={c}
          transform={`rotate(${a} 50 50)`}
        />
      ))}
    </svg>
  ),
};

const SLOTS = {
  left: { left: "13%", x: "-50%", scale: 0.8, rotate: -5, zIndex: 10, opacity: 0.9 },
  center: { left: "50%", x: "-50%", scale: 1, rotate: 0, zIndex: 30, opacity: 1 },
  right: { left: "87%", x: "-50%", scale: 0.8, rotate: 5, zIndex: 10, opacity: 0.9 },
  hidden: { left: "50%", x: "-50%", scale: 0.7, rotate: 0, zIndex: 0, opacity: 0 },
};

function goToForm(e) {
  e.preventDefault();
  document.getElementById("waitlist")?.scrollIntoView({ behavior: "smooth", block: "start" });
}

export default function BeliefsDeck() {
  const [index, setIndex] = useState(0);

  const next = () => setIndex((i) => (i + 1) % BELIEFS.length);
  const prev = () => setIndex((i) => (i - 1 + BELIEFS.length) % BELIEFS.length);
  const current = BELIEFS[index];
  const upcoming = BELIEFS[(index + 1) % BELIEFS.length];

  return (
    <section data-testid="beliefs-section" className="relative mx-auto max-w-6xl overflow-x-clip px-6 py-28 sm:py-36">
      <motion.div
        aria-hidden="true"
        animate={{ backgroundColor: current.orb }}
        transition={{ duration: 1, ease: "easeInOut" }}
        className="pointer-events-none absolute -left-20 top-24 h-72 w-72 rounded-full opacity-60 blur-3xl"
      />
      <motion.div
        aria-hidden="true"
        animate={{ backgroundColor: upcoming.orb }}
        transition={{ duration: 1, ease: "easeInOut" }}
        className="pointer-events-none absolute -right-20 bottom-16 h-80 w-80 rounded-full opacity-60 blur-3xl"
      />

      <motion.h2
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: EASE }}
        className="relative mx-auto max-w-3xl text-center font-sans text-[1.35rem] font-bold leading-[1.2] tracking-tight text-[#171512] sm:text-4xl"
      >
        JOIN ONLY IF YOU BELIEVE THESE THINGS, FUNDAMENTALLY.
      </motion.h2>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
        className="relative mt-16"
      >
        <div
          data-testid="beliefs-deck"
          className="relative mx-auto h-[450px] w-full max-w-[960px] select-none sm:h-[490px]"
        >
          {BELIEFS.map((b, i) => {
            const rel = (i - index + BELIEFS.length) % BELIEFS.length;
            const slot = rel === 0 ? "center" : rel === 1 ? "right" : rel === 3 ? "left" : "hidden";
            const s = SLOTS[slot];
            const handleClick =
              slot === "center" ? next : slot === "right" ? next : slot === "left" ? prev : undefined;
            return (
              <motion.article
                key={b.n}
                data-testid={`belief-card-${b.n}`}
                data-slot={slot}
                onClick={handleClick}
                role="button"
                aria-label={slot === "center" ? `Belief ${b.n}. Show the next belief card` : `Show belief ${b.n}`}
                animate={{
                  left: s.left,
                  x: s.x,
                  scale: s.scale,
                  rotate: s.rotate,
                  opacity: s.opacity,
                  zIndex: s.zIndex,
                }}
                whileHover={slot === "center" ? { scale: 1.03 } : { scale: 0.84 }}
                transition={{ type: "spring", stiffness: 210, damping: 22 }}
                style={{ background: b.bg, boxShadow: "0 30px 60px -32px rgba(23,21,18,0.35)" }}
                className="absolute top-0 flex h-[430px] w-[290px] cursor-pointer flex-col overflow-hidden rounded-[26px] p-7 sm:h-[470px] sm:w-[380px] sm:p-9"
              >
                <span className="font-mono text-[10px] font-bold tracking-[0.3em] text-[#171512]/60">
                  NO. {b.n}
                </span>
                <h3 className="mt-4 font-sans text-[1.35rem] font-bold leading-[1.15] tracking-tight text-[#171512] sm:text-[1.9rem]">
                  {b.text}
                </h3>
                {b.tag && (
                  <span className="mt-2 font-mono text-[10px] tracking-[0.06em] text-[#171512]/40">
                    {b.tag}
                  </span>
                )}
                {b.sub && (
                  <p className="mt-3 max-w-[16rem] text-[13px] leading-[1.55] text-[#171512]/65 sm:mt-4 sm:text-[15px]">
                    {b.sub}
                  </p>
                )}
                <div className="pointer-events-none absolute -bottom-8 -right-8 h-44 w-44 sm:h-56 sm:w-56" aria-hidden="true">
                  {GRAPHICS[b.icon](b.deep)}
                </div>
                <span className="mt-auto font-serif text-xl italic leading-none text-[#171512]">
                  nostos
                </span>
              </motion.article>
            );
          })}
        </div>

        <div className="mt-10 flex items-center justify-center gap-2.5">
          {BELIEFS.map((b, i) => (
            <button
              key={b.n}
              data-testid={`belief-dot-${b.n}`}
              onClick={() => setIndex(i)}
              aria-label={`Show belief ${b.n}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === index
                  ? "w-7 bg-[#171512]"
                  : "w-1.5 bg-[#171512]/20 hover:bg-[#171512]/40"
              }`}
            />
          ))}
        </div>
      </motion.div>

      <p className="relative mt-8 text-center font-mono text-[11px] uppercase tracking-[0.22em] text-[#171512]/40">
        click the cards to move between beliefs
      </p>

      <p className="relative mt-8 text-center">
        <a
          href="#waitlist"
          onClick={goToForm}
          data-testid="beliefs-join-button"
          className="inline-block rounded-full bg-[#171512] px-5 py-2.5 font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-[#FDFBF6] transition-colors duration-200 hover:bg-[#F5C7D4] hover:text-[#171512]"
        >
          join now
        </a>
      </p>
    </section>
  );
}
